import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { BottomNav } from '../components/custom/BottomNav';
import { TouchDial } from '../components/custom/TouchDial';
import { VoiceInputInline } from '../components/custom/VoiceInput';
import OfflineIndicator from '../components/custom/OfflineIndicator';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    ArrowLeft,
    Play,
    Square,
    Timer,
    Clock,
    Download,
    Trash2,
    Plus,
    Bell,
    AlertTriangle,
    CheckCircle,
    MoreVertical,
    ChevronRight,
    PenLine,
    DollarSign,
    Wallet,
    CreditCard,
    Receipt,
    FileText,
    Calendar as CalendarIcon,
    Ban,
    RotateCcw,
    FileCheck,
    Lock,
    Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_TYPES = [
    {
        code: "L100",
        name: "Case Analysis & Strategy",
        description: "Analysis of case facts and applicable law to assess issues and develop strategy."
    },
    {
        code: "L110",
        name: "Legal Research",
        description: "Research and analysis of statutes, case law, regulations, and related legal authorities."
    },
    {
        code: "L120",
        name: "Document Drafting & Review",
        description: "Drafting, reviewing, and revising pleadings, motions, agreements, correspondence, and other documents."
    },
    {
        code: "L130",
        name: "Discovery & Evidence Review",
        description: "Preparation, review, and analysis of discovery materials, documents, and evidence."
    },
    {
        code: "L140",
        name: "Witness & Deposition Preparation",
        description: "Preparation for and participation in witness interviews, depositions, and expert-related activities."
    },
    {
        code: "L150",
        name: "Court Appearances & Proceedings",
        description: "Preparation for and attendance at court hearings, proceedings, trials, and oral arguments."
    },
    {
        code: "L160",
        name: "Negotiation & Resolution Efforts",
        description: "Negotiation with opposing parties and participation in settlement or alternative dispute resolution proceedings."
    },
    {
        code: "L170",
        name: "Client & Third-Party Communications",
        description: "Communications with the client, opposing counsel, court personnel, experts, and other third parties regarding the matter."
    },
    {
        code: "L180",
        name: "Case Management & Administration",
        description: "Case organization, scheduling, coordination, supervision, and related administrative activities necessary to advance the matter."
    },
    {
        code: "L190",
        name: "Travel & Waiting Time",
        description: "Travel and waiting time related to court appearances, depositions, meetings, or other matter-related activities."
    },
    {
        code: "L300",
        name: "Investigations & Compliance Review",
        description: "Conducting interviews, reviewing records, analyzing findings, and preparing reports in connection with an internal investigation or compliance review."
    }
];

// Helper to get display label for task type
const getTaskLabel = (task) => `${task.code} - ${task.name}`;
const getTaskByCode = (code) => TASK_TYPES.find(t => t.code === code) || TASK_TYPES[0];

const CASE_STATUSES = ["Open", "In Progress", "Pending", "Closed"];

const statusColors = {
    'Open': 'bg-emerald-100 text-emerald-700',
    'In Progress': 'bg-amber-100 text-amber-700',
    'Pending': 'bg-orange-100 text-orange-700',
    'Closed': 'bg-slate-100 text-slate-600',
};

export default function CaseDetail() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeaders } = useAuth();
    
    // Offline sync hook
    const { 
        isOnline, 
        isSyncing, 
        pendingCount, 
        queueTimeEntry, 
        triggerSync 
    } = useOfflineSync(getAuthHeaders);

    const [caseData, setCaseData] = useState(null);
    const [entries, setEntries] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [statuteTemplates, setStatuteTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeEntry, setActiveEntry] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [activeTab, setActiveTab] = useState('timer'); // 'timer', 'entries', 'reminders'
    
    // Timer state
    const [selectedTask, setSelectedTask] = useState(TASK_TYPES[0].code);
    const [notes, setNotes] = useState('');
    const [isStarting, setIsStarting] = useState(false);
    const [isStopping, setIsStopping] = useState(false);
    const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
    const [stopNotes, setStopNotes] = useState('');
    
    // Manual entry state
    const [isManualOpen, setIsManualOpen] = useState(false);
    const [dialMinutes, setDialMinutes] = useState(60);
    const [manualTask, setManualTask] = useState(TASK_TYPES[0].code);
    const [manualNotes, setManualNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Reminder state
    const [isReminderOpen, setIsReminderOpen] = useState(false);
    const [isStatuteOpen, setIsStatuteOpen] = useState(false);
    const [reminderTitle, setReminderTitle] = useState('');
    const [reminderDescription, setReminderDescription] = useState('');
    const [reminderDueDate, setReminderDueDate] = useState('');
    const [reminderIsCritical, setReminderIsCritical] = useState(false);
    const [selectedCaseType, setSelectedCaseType] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [incidentDate, setIncidentDate] = useState('');
    
    // Delete state
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isDeleteEntryOpen, setIsDeleteEntryOpen] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);
    
    // Retainer payment state
    const [isRetainerOpen, setIsRetainerOpen] = useState(false);
    const [retainerPayment, setRetainerPayment] = useState('');
    const [retainerNotes, setRetainerNotes] = useState('');
    
    // Export state
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [exportDateAccordion, setExportDateAccordion] = useState(null); // 'from' | 'to' | null
    
    // Invoice state
    const [invoices, setInvoices] = useState([]);
    const [isInvoicesOpen, setIsInvoicesOpen] = useState(false);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [invoicePreview, setInvoicePreview] = useState(null);
    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
    const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
    const [isInvoiceDetailsOpen, setIsInvoiceDetailsOpen] = useState(false);
    
    // Payment confirmation state
    const [isPaymentConfirmOpen, setIsPaymentConfirmOpen] = useState(false);
    const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState(null);
    
    // Delete invoice state
    const [showDeleteInvoiceConfirm, setShowDeleteInvoiceConfirm] = useState(false);
    const [deleteInvoiceConfirmText, setDeleteInvoiceConfirmText] = useState('');
    const [isDeletingInvoice, setIsDeletingInvoice] = useState(false);
    
    // Manual statute state
    const [isManualStatuteOpen, setIsManualStatuteOpen] = useState(false);
    const [manualStatuteTitle, setManualStatuteTitle] = useState('');
    const [manualStatuteDescription, setManualStatuteDescription] = useState('');
    const [manualStatuteIncidentDate, setManualStatuteIncidentDate] = useState('');
    const [manualStatuteDeadline, setManualStatuteDeadline] = useState('');

    const timerRef = useRef(null);

    const fetchCase = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/cases/${caseId}`, {
                headers: getAuthHeaders()
            });
            setCaseData(response.data);
            if (response.data.case_type && response.data.case_type !== 'Other') {
                setSelectedCaseType(response.data.case_type);
            }
        } catch (error) {
            toast.error('Failed to load case');
            navigate('/dashboard');
        }
    }, [caseId, getAuthHeaders, navigate]);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/time-entries/case/${caseId}`, {
                headers: getAuthHeaders()
            });
            setEntries(response.data);
        } catch (error) {
            toast.error('Failed to load time entries');
        }
    }, [caseId, getAuthHeaders]);

    const fetchActiveTimer = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/time-entries/active/${caseId}`, {
                headers: getAuthHeaders()
            });
            if (response.data && response.data.active_timer) {
                setActiveEntry(response.data.active_timer);
                setNotes(response.data.active_timer.notes || response.data.active_timer.description || '');
            } else {
                setActiveEntry(null);
            }
        } catch {
            setActiveEntry(null);
        }
    }, [caseId, getAuthHeaders]);

    const fetchReminders = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/reminders/case/${caseId}`, {
                headers: getAuthHeaders()
            });
            setReminders(response.data);
        } catch {
            // Silent fail
        }
    }, [caseId, getAuthHeaders]);

    const fetchStatuteTemplates = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/statute-templates`, {
                headers: getAuthHeaders()
            });
            setStatuteTemplates(response.data.templates);
        } catch {
            // Silent fail
        }
    }, [getAuthHeaders]);

    const fetchInvoices = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/invoices/case/${caseId}`, {
                headers: getAuthHeaders()
            });
            setInvoices(response.data);
        } catch {
            // Silent fail
        }
    }, [caseId, getAuthHeaders]);

    // Check if case is closed (read-only mode)
    const isCaseClosed = caseData?.status === 'Closed';

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await Promise.all([fetchCase(), fetchEntries(), fetchActiveTimer(), fetchReminders(), fetchStatuteTemplates(), fetchInvoices()]);
            setIsLoading(false);
        };
        loadData();
    }, [fetchCase, fetchEntries, fetchActiveTimer, fetchReminders, fetchStatuteTemplates, fetchInvoices]);

    // Timer effect
    useEffect(() => {
        if (activeEntry) {
            const startTime = new Date(activeEntry.start_time);
            const updateElapsed = () => {
                setElapsedTime(Math.floor((new Date() - startTime) / 1000));
            };
            updateElapsed();
            timerRef.current = setInterval(updateElapsed, 1000);
            return () => clearInterval(timerRef.current);
        } else {
            setElapsedTime(0);
        }
    }, [activeEntry]);

    const handleStartTimer = async () => {
        setIsStarting(true);
        try {
            const response = await axios.post(`${API}/time-entries/start`, {
                case_id: caseId,
                task_type: selectedTask,
                notes: notes
            }, { headers: getAuthHeaders() });
            setActiveEntry(response.data);
            toast.success('Timer started');
            fetchCase();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start timer');
        } finally {
            setIsStarting(false);
        }
    };

    const handleStopTimer = async () => {
        if (!activeEntry) return;
        setIsStopping(true);
        try {
            await axios.post(`${API}/time-entries/stop`, {
                entry_id: activeEntry.id,
                notes: stopNotes
            }, { headers: getAuthHeaders() });
            setActiveEntry(null);
            setNotes('');
            setStopNotes('');
            setIsStopDialogOpen(false);
            toast.success('Timer stopped');
            fetchEntries();
            fetchCase();
        } catch (error) {
            toast.error('Failed to stop timer');
        } finally {
            setIsStopping(false);
        }
    };

    const handleManualEntry = async () => {
        const entryData = {
            case_id: caseId,
            task_type: manualTask,
            duration_minutes: dialMinutes,
            notes: manualNotes
        };
        
        setIsSubmitting(true);
        
        // If offline, queue for later sync
        if (!isOnline) {
            const success = await queueTimeEntry(entryData);
            if (success) {
                setIsManualOpen(false);
                setManualNotes('');
            }
            setIsSubmitting(false);
            return;
        }
        
        // Online - submit directly
        try {
            await axios.post(`${API}/time-entries/manual`, entryData, { 
                headers: getAuthHeaders() 
            });
            
            toast.success(`${dialMinutes} minutes added`);
            setIsManualOpen(false);
            setManualNotes('');
            fetchEntries();
            fetchCase();
        } catch (error) {
            // If network error, try to queue offline
            if (!navigator.onLine || error.code === 'ERR_NETWORK') {
                const success = await queueTimeEntry(entryData);
                if (success) {
                    setIsManualOpen(false);
                    setManualNotes('');
                }
            } else {
                toast.error('Failed to add time');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateReminder = async () => {
        if (!reminderTitle || !reminderDueDate) {
            toast.error('Please fill in all required fields');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/reminders`, {
                case_id: caseId,
                title: reminderTitle,
                description: reminderDescription,
                due_date: reminderDueDate,
                is_critical: reminderIsCritical
            }, { headers: getAuthHeaders() });
            toast.success('Reminder created');
            setIsReminderOpen(false);
            setReminderTitle('');
            setReminderDescription('');
            setReminderDueDate('');
            setReminderIsCritical(false);
            fetchReminders();
        } catch (error) {
            toast.error('Failed to create reminder');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleApplyStatute = async () => {
        if (!selectedCaseType || !selectedTemplate || !incidentDate) {
            toast.error('Please fill in all fields');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/reminders/apply-template`, {
                case_id: caseId,
                case_type: selectedCaseType,
                template_name: selectedTemplate,
                incident_date: incidentDate
            }, { headers: getAuthHeaders() });
            toast.success('Deadline added');
            setIsStatuteOpen(false);
            setSelectedTemplate('');
            setIncidentDate('');
            fetchReminders();
        } catch (error) {
            toast.error('Failed to apply template');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleReminder = async (reminderId, currentStatus) => {
        try {
            await axios.put(`${API}/reminders/${reminderId}`, {
                is_completed: !currentStatus
            }, { headers: getAuthHeaders() });
            fetchReminders();
            toast.success(currentStatus ? 'Marked incomplete' : 'Completed');
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const handleDeleteReminder = async (reminderId) => {
        try {
            await axios.delete(`${API}/reminders/${reminderId}`, { headers: getAuthHeaders() });
            toast.success('Deleted');
            fetchReminders();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const confirmDeleteEntry = (entry) => {
        setEntryToDelete(entry);
        setIsDeleteEntryOpen(true);
    };

    const handleDeleteEntry = async () => {
        if (!entryToDelete) return;
        try {
            await axios.delete(`${API}/time-entries/${entryToDelete.id}`, { headers: getAuthHeaders() });
            toast.success('Entry deleted');
            setIsDeleteEntryOpen(false);
            setEntryToDelete(null);
            fetchEntries();
            fetchCase();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleDeleteCase = async () => {
        try {
            await axios.delete(`${API}/cases/${caseId}`, { headers: getAuthHeaders() });
            toast.success('Case deleted');
            navigate('/dashboard');
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await axios.put(`${API}/cases/${caseId}`, { status: newStatus }, { headers: getAuthHeaders() });
            setCaseData(prev => ({ ...prev, status: newStatus }));
            toast.success('Status updated');
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    // Calculate date range of existing entries for export UI hint
    const getEntriesDateRange = () => {
        const completedEntries = entries.filter(e => !e.is_running);
        if (completedEntries.length === 0) return null;
        
        const dates = completedEntries
            .map(e => e.start_time || e.created_at)
            .filter(Boolean)
            .map(d => d.slice(0, 10))
            .sort();
        
        if (dates.length === 0) return null;
        return { start: dates[0], end: dates[dates.length - 1] };
    };

    const handleExportCSV = async () => {
        setIsExporting(true);
        try {
            let url = `${API}/time-entries/export/${caseId}`;
            const params = new URLSearchParams();
            if (exportStartDate) params.append('start_date', exportStartDate);
            if (exportEndDate) params.append('end_date', exportEndDate);
            if (params.toString()) url += `?${params.toString()}`;
            
            const response = await axios.get(url, {
                headers: getAuthHeaders(),
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `${caseData?.name?.replace(/\s+/g, '_')}_time.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('CSV Downloaded');
            setIsExportOpen(false);
        } catch (error) {
            toast.error('Export failed');
        } finally {
            setIsExporting(false);
        }
    };

    const handleWaiveEntry = async (entryId, currentWaived) => {
        try {
            await axios.put(`${API}/time-entries/${entryId}/waive`, {
                is_waived: !currentWaived,
                waive_reason: !currentWaived ? "Courtesy - No Charge" : null
            }, { headers: getAuthHeaders() });
            
            toast.success(currentWaived ? 'Entry restored to billable' : 'Entry marked as no charge');
            fetchEntries();
            fetchCase();
        } catch (error) {
            toast.error('Failed to update entry');
        }
    };

    const handleCreateInvoice = async (force = false) => {
        setIsExporting(true);
        try {
            // First, preview the invoice to check for already-invoiced entries
            if (!force) {
                let previewUrl = `${API}/time-entries/invoice-preview/${caseId}`;
                const previewParams = new URLSearchParams();
                if (exportStartDate) previewParams.append('start_date', exportStartDate);
                if (exportEndDate) previewParams.append('end_date', exportEndDate);
                if (previewParams.toString()) previewUrl += `?${previewParams.toString()}`;
                
                const previewResponse = await axios.get(previewUrl, { headers: getAuthHeaders() });
                setInvoicePreview(previewResponse.data);
                
                // If there are already-invoiced entries, show warning
                if (previewResponse.data.already_invoiced_count > 0) {
                    setShowDuplicateWarning(true);
                    setIsExporting(false);
                    return;
                }
                
                // If no entries to invoice
                if (previewResponse.data.entry_count === 0) {
                    const dateRange = getEntriesDateRange();
                    if (dateRange && (exportStartDate || exportEndDate)) {
                        toast.error(`No entries in selected dates. Entries exist from ${format(new Date(dateRange.start), 'MMM d')} – ${format(new Date(dateRange.end), 'MMM d')}`);
                    } else {
                        toast.error('No time entries to invoice');
                    }
                    setIsExporting(false);
                    return;
                }
            }
            
            // Create invoice record
            await axios.post(`${API}/invoices`, {
                case_id: caseId,
                start_date: exportStartDate || null,
                end_date: exportEndDate || null
            }, { headers: getAuthHeaders() });
            
            // Download PDF with mark_as_invoiced and force flags
            let url = `${API}/time-entries/invoice/${caseId}`;
            const params = new URLSearchParams();
            if (exportStartDate) params.append('start_date', exportStartDate);
            if (exportEndDate) params.append('end_date', exportEndDate);
            params.append('mark_as_invoiced', 'true');
            if (force) params.append('force', 'true');
            url += `?${params.toString()}`;
            
            const response = await axios.get(url, {
                headers: getAuthHeaders(),
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `Invoice_${caseData?.name?.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Invoice created & downloaded');
            setIsExportOpen(false);
            setShowDuplicateWarning(false);
            setInvoicePreview(null);
            fetchInvoices();
            fetchEntries(); // Refresh entries to show invoiced status
        } catch (error) {
            if (error.response?.status === 409) {
                // Already invoiced - should be handled by preview, but fallback
                toast.error('Some entries have already been invoiced');
            } else {
                toast.error('Failed to create invoice');
            }
        } finally {
            setIsExporting(false);
        }
    };

    const handleViewInvoiceDetails = (invoice) => {
        setSelectedInvoiceDetails(invoice);
        setIsInvoiceDetailsOpen(true);
    };

    const handleDownloadInvoice = async (invoice) => {
        try {
            // Use the invoice's date range if available, otherwise download all
            let url = `${API}/time-entries/invoice/${caseId}`;
            const params = new URLSearchParams();
            if (invoice.start_date) params.append('start_date', invoice.start_date);
            if (invoice.end_date) params.append('end_date', invoice.end_date);
            params.append('invoice_number', invoice.invoice_number);
            params.append('mark_as_invoiced', 'false'); // Don't re-mark as invoiced
            params.append('force', 'true'); // Allow downloading even if already invoiced
            url += `?${params.toString()}`;
            
            const response = await axios.get(url, {
                headers: getAuthHeaders(),
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `${invoice.invoice_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Invoice downloaded');
        } catch (error) {
            toast.error('Failed to download invoice');
        }
    };

    const confirmMarkPaid = (invoice) => {
        setInvoiceToMarkPaid(invoice);
        setIsPaymentConfirmOpen(true);
    };

    const handleMarkInvoicePaid = async () => {
        if (!invoiceToMarkPaid) return;
        try {
            await axios.put(`${API}/invoices/${invoiceToMarkPaid.id}`, {
                status: 'paid'
            }, { headers: getAuthHeaders() });
            toast.success('Invoice marked as paid');
            setIsPaymentConfirmOpen(false);
            setInvoiceToMarkPaid(null);
            fetchInvoices();
        } catch {
            toast.error('Failed to update invoice');
        }
    };

    const handleDeleteInvoice = async () => {
        if (!selectedInvoiceDetails || deleteInvoiceConfirmText !== 'DELETE') return;
        
        setIsDeletingInvoice(true);
        try {
            await axios.delete(`${API}/invoices/${selectedInvoiceDetails.id}`, { 
                headers: getAuthHeaders() 
            });
            toast.success('Invoice deleted');
            setShowDeleteInvoiceConfirm(false);
            setDeleteInvoiceConfirmText('');
            setIsInvoiceDetailsOpen(false);
            setSelectedInvoiceDetails(null);
            fetchInvoices();
        } catch (error) {
            toast.error('Failed to delete invoice');
        } finally {
            setIsDeletingInvoice(false);
        }
    };

    const handleCreateManualStatute = async () => {
        if (!manualStatuteTitle || !manualStatuteDeadline) {
            toast.error('Please enter a title and deadline date');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/reminders`, {
                case_id: caseId,
                title: manualStatuteTitle,
                description: manualStatuteDescription || `Incident date: ${manualStatuteIncidentDate || 'Not specified'}`,
                due_date: manualStatuteDeadline,
                reminder_type: 'statute_of_limitations',
                category: 'Custom',
                is_critical: true
            }, { headers: getAuthHeaders() });
            toast.success('Statute deadline added');
            setIsManualStatuteOpen(false);
            setManualStatuteTitle('');
            setManualStatuteDescription('');
            setManualStatuteIncidentDate('');
            setManualStatuteDeadline('');
            fetchReminders();
        } catch (error) {
            toast.error('Failed to add deadline');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleExportInvoice = async (force = false) => {
        setIsExporting(true);
        try {
            // First, preview the invoice to check for already-invoiced entries
            if (!force) {
                let previewUrl = `${API}/time-entries/invoice-preview/${caseId}`;
                const previewParams = new URLSearchParams();
                if (exportStartDate) previewParams.append('start_date', exportStartDate);
                if (exportEndDate) previewParams.append('end_date', exportEndDate);
                if (previewParams.toString()) previewUrl += `?${previewParams.toString()}`;
                
                const previewResponse = await axios.get(previewUrl, { headers: getAuthHeaders() });
                setInvoicePreview(previewResponse.data);
                
                // If there are already-invoiced entries, show warning
                if (previewResponse.data.already_invoiced_count > 0) {
                    setShowDuplicateWarning(true);
                    setIsExporting(false);
                    return;
                }
                
                // If no entries to invoice
                if (previewResponse.data.entry_count === 0) {
                    const dateRange = getEntriesDateRange();
                    if (dateRange && (exportStartDate || exportEndDate)) {
                        toast.error(`No entries in selected dates. Entries exist from ${format(new Date(dateRange.start), 'MMM d')} – ${format(new Date(dateRange.end), 'MMM d')}`);
                    } else {
                        toast.error('No time entries to invoice');
                    }
                    setIsExporting(false);
                    return;
                }
            }
            
            let url = `${API}/time-entries/invoice/${caseId}`;
            const params = new URLSearchParams();
            if (exportStartDate) params.append('start_date', exportStartDate);
            if (exportEndDate) params.append('end_date', exportEndDate);
            params.append('mark_as_invoiced', 'true');
            if (force) params.append('force', 'true');
            url += `?${params.toString()}`;
            
            const response = await axios.get(url, {
                headers: getAuthHeaders(),
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `Invoice_${caseData?.name?.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Invoice PDF Downloaded');
            setIsExportOpen(false);
            setShowDuplicateWarning(false);
            setInvoicePreview(null);
            fetchEntries(); // Refresh entries to show invoiced status
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error('Some entries have already been invoiced');
            } else {
                toast.error('Invoice generation failed');
            }
        } finally {
            setIsExporting(false);
        }
    };

    const handleAddRetainer = async () => {
        if (!retainerPayment || parseFloat(retainerPayment) <= 0) {
            toast.error('Enter a valid amount');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/cases/${caseId}/retainer-payment`, {
                case_id: caseId,
                amount: parseFloat(retainerPayment),
                notes: retainerNotes
            }, { headers: getAuthHeaders() });
            toast.success('Retainer added');
            setIsRetainerOpen(false);
            setRetainerPayment('');
            setRetainerNotes('');
            fetchCase();
        } catch (error) {
            toast.error('Failed to add retainer');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatMoney = (amount) => {
        if (amount === null || amount === undefined) return '$0';
        return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatElapsed = (seconds) => {
        if (!seconds || isNaN(seconds) || seconds < 0) {
            return '00:00:00';
        }
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '-';
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    };

    const formatDateTime = (iso) => {
        if (!iso) return '-';
        try { return format(new Date(iso), 'MMM d, h:mm a'); } catch { return '-'; }
    };

    const getDaysUntil = (due) => {
        try {
            const today = new Date(); today.setHours(0,0,0,0);
            const d = new Date(due); d.setHours(0,0,0,0);
            return Math.ceil((d - today) / 86400000);
        } catch { return null; }
    };

    const currentTemplates = selectedCaseType && statuteTemplates[selectedCaseType] || [];

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-[#1C6EE7] animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pb-safe">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
                <div className="px-6 py-4">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-secondary rounded-full">
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <div className="flex-1 min-w-0">
                            <h1 className="font-heading text-xl font-semibold truncate" data-testid="case-title">
                                {caseData?.name}
                            </h1>
                            <div className="flex items-center gap-2">
                                {caseData?.client_name && (
                                    <p className="text-sm text-muted-foreground truncate">{caseData.client_name}</p>
                                )}
                                {caseData?.is_shared && (
                                    <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                                        Shared • {caseData.owner_name}
                                    </Badge>
                                )}
                            </div>
                        </div>
                        <OfflineIndicator 
                            isOnline={isOnline}
                            isSyncing={isSyncing}
                            pendingCount={pendingCount}
                            onSync={triggerSync}
                        />
                        <Select value={caseData?.status} onValueChange={handleStatusChange}>
                            <SelectTrigger className="w-auto gap-2 h-9 rounded-full border-0 bg-secondary px-4 shrink-0">
                                <Badge className={`${statusColors[caseData?.status]} border-0`}>
                                    {caseData?.status}
                                </Badge>
                            </SelectTrigger>
                            <SelectContent>
                                {CASE_STATUSES.map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Tab Switcher */}
                <div className="px-6 pb-3">
                    <div className="flex gap-1 bg-secondary rounded-full p-1">
                        {[
                            { key: 'timer', label: 'Timer', icon: Timer },
                            { key: 'entries', label: 'Entries', icon: Clock, count: entries.length },
                            { key: 'invoices', label: 'Invoices', icon: Receipt, count: invoices.filter(i => i.status !== 'paid').length },
                            { key: 'reminders', label: 'Deadlines', icon: Bell, count: reminders.filter(r => !r.is_completed).length },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-full text-sm font-medium transition-all ${
                                    activeTab === tab.key 
                                        ? 'bg-white text-foreground shadow-sm' 
                                        : 'text-muted-foreground'
                                }`}
                                data-testid={`tab-${tab.key}`}
                            >
                                <tab.icon className="h-4 w-4" />
                                <span className="hidden sm:inline">{tab.label}</span>
                                {tab.count > 0 && (
                                    <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                                        {tab.count}
                                    </Badge>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <main className="px-6 py-6">
                {/* Closed Case Banner */}
                {isCaseClosed && (
                    <div className="mb-6 bg-slate-100 rounded-2xl p-4 flex items-center gap-3">
                        <div className="p-2 bg-slate-200 rounded-full">
                            <Lock className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                            <p className="font-medium text-slate-700 text-sm">Case Closed</p>
                            <p className="text-xs text-slate-500">This case is read-only. Reopen to make changes.</p>
                        </div>
                    </div>
                )}

                {/* Timer Tab */}
                {activeTab === 'timer' && (
                    <div className="space-y-6 animate-fade-in-up">
                        {/* Active Timer Display */}
                        {activeEntry ? (
                            <div className="bg-[#1C6EE7] rounded-3xl p-8 text-white text-center timer-running">
                                <p className="text-sm text-white/80 mb-2">{getTaskByCode(activeEntry.task_type)?.name || activeEntry.task_type}</p>
                                <p className="font-display text-5xl font-bold tracking-tight mb-6" data-testid="timer-display">
                                    {formatElapsed(elapsedTime)}
                                </p>
                                <button
                                    onClick={() => { setStopNotes(activeEntry.notes || ''); setIsStopDialogOpen(true); }}
                                    disabled={isStopping}
                                    className="w-full py-4 bg-white text-[#1C6EE7] rounded-full font-semibold text-lg hover:bg-white/90 transition-all active:scale-95"
                                    data-testid="stop-timer-btn"
                                >
                                    <Square className="h-5 w-5 inline mr-2" />
                                    {isStopping ? 'Stopping...' : 'Stop Timer'}
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Start Timer Form */}
                                <div className="bg-white rounded-3xl p-6 shadow-soft space-y-5">
                                    <div className="text-center">
                                        <p className="font-display text-5xl font-bold text-muted-foreground/30" data-testid="timer-display">
                                            00:00:00
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Task Type</Label>
                                        <Select value={selectedTask} onValueChange={setSelectedTask}>
                                            <SelectTrigger className="h-12 rounded-xl" data-testid="task-type-select">
                                                <SelectValue>
                                                    {getTaskByCode(selectedTask) && (
                                                        <span className="font-medium">{getTaskLabel(getTaskByCode(selectedTask))}</span>
                                                    )}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[300px]">
                                                {TASK_TYPES.map(t => (
                                                    <SelectItem key={t.code} value={t.code} className="py-2.5">
                                                        <span className="font-medium">{t.code} - {t.name}</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {getTaskByCode(selectedTask) && (
                                            <p className="text-xs text-muted-foreground px-1">
                                                {getTaskByCode(selectedTask).description}
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            Notes (optional)
                                            <span className="text-xs text-muted-foreground">• tap mic to dictate</span>
                                        </Label>
                                        <VoiceInputInline
                                            value={notes}
                                            onChange={setNotes}
                                            placeholder="What are you working on?"
                                        />
                                    </div>

                                    <button
                                        onClick={handleStartTimer}
                                        disabled={isStarting || isCaseClosed}
                                        className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid="start-timer-btn"
                                    >
                                        <Play className="h-5 w-5 inline mr-2" />
                                        {isStarting ? 'Starting...' : isCaseClosed ? 'Case Closed' : 'Start Timer'}
                                    </button>
                                </div>

                                {/* Manual Entry Button */}
                                <button
                                    onClick={() => setIsManualOpen(true)}
                                    disabled={isCaseClosed}
                                    className="w-full py-4 bg-white rounded-2xl shadow-soft text-muted-foreground font-medium flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="manual-entry-btn"
                                >
                                    <PenLine className="h-5 w-5" />
                                    Add Manual Entry
                                </button>
                            </>
                        )}

                        {/* Stats */}
                        <div className="bg-white rounded-3xl p-6 shadow-soft space-y-4">
                            {/* Time & Export Row */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Time</p>
                                    <p className="font-display text-3xl font-bold">{formatDuration(caseData?.total_minutes || 0)}</p>
                                </div>
                                <button
                                    onClick={() => setIsExportOpen(true)}
                                    disabled={entries.filter(e => !e.is_running).length === 0}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-[#1C6EE7] text-white rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="export-btn"
                                >
                                    <Receipt className="h-4 w-4" />
                                    <span className="font-medium text-sm">Invoice</span>
                                </button>
                            </div>

                            {/* Billing Info */}
                            {caseData && (
                                <div className="pt-4 border-t space-y-3">
                                    {/* Billing Type Badge */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Billing</span>
                                        <Badge variant="outline" className={`capitalize ${
                                            caseData.billing_type === 'flat_fee' ? 'bg-blue-50 text-[#1C6EE7] border-blue-200' :
                                            caseData.billing_type === 'hourly' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                            'bg-slate-50 text-slate-700'
                                        }`}>
                                            {caseData.billing_type === 'flat_fee' ? 'Flat Fee' : 
                                             caseData.billing_type === 'hourly' ? `$${caseData.hourly_rate}/hr` :
                                             caseData.billing_type}
                                        </Badge>
                                    </div>

                                    {/* Total Billed */}
                                    {caseData.billing_type !== 'flat_fee' && caseData.hourly_rate > 0 && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground flex items-center gap-2">
                                                <Receipt className="h-4 w-4" />
                                                Total Billed
                                            </span>
                                            <span className="font-display font-semibold text-lg">
                                                {formatMoney(caseData.total_billed)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Flat Fee Amount */}
                                    {caseData.billing_type === 'flat_fee' && caseData.flat_fee_amount > 0 && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground flex items-center gap-2">
                                                <DollarSign className="h-4 w-4" />
                                                Flat Fee
                                            </span>
                                            <span className="font-display font-semibold text-lg">
                                                {formatMoney(caseData.flat_fee_amount)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Retainer Balance */}
                                    {caseData.billing_type !== 'flat_fee' && caseData.billing_type !== 'contingency' && (
                                        <div className="bg-blue-50 rounded-2xl p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium flex items-center gap-2 text-indigo-700">
                                                    <Wallet className="h-4 w-4" />
                                                    Retainer Balance
                                                </span>
                                                <button
                                                    onClick={() => setIsRetainerOpen(true)}
                                                    className="text-xs bg-white px-3 py-1 rounded-full font-medium text-[#1C6EE7] hover:bg-blue-100 transition-colors flex items-center gap-1"
                                                    data-testid="add-retainer-btn"
                                                >
                                                    <CreditCard className="h-3 w-3" />
                                                    Add Funds
                                                </button>
                                            </div>
                                            <p className="font-display text-2xl font-bold text-indigo-700" data-testid="retainer-balance">
                                                {formatMoney(caseData.retainer_balance)}
                                            </p>
                                            {caseData.retainer_amount > 0 && (
                                                <p className="text-xs text-[#1C6EE7] mt-1">
                                                    of {formatMoney(caseData.retainer_amount)} total deposited
                                                </p>
                                            )}
                                            {caseData.retainer_balance <= 0 && caseData.total_billed > 0 && (
                                                <p className="text-xs text-amber-600 mt-2 font-medium">
                                                    ⚠️ Retainer exhausted - additional billing applies
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Entries Tab */}
                {activeTab === 'entries' && (
                    <div className="space-y-3 animate-fade-in-up">
                        {entries.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-3xl shadow-soft">
                                <Clock className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                                <p className="text-muted-foreground">No time entries yet</p>
                            </div>
                        ) : (
                            entries.map(entry => (
                                <div 
                                    key={entry.id} 
                                    className={`rounded-2xl p-4 shadow-soft transition-all ${
                                        entry.is_waived 
                                            ? 'bg-slate-50 border border-slate-200' 
                                            : 'bg-white'
                                    }`}
                                    data-testid={`entry-${entry.id}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className={`font-medium ${entry.is_waived ? 'line-through text-muted-foreground' : ''}`}>
                                                    {getTaskByCode(entry.task_type)?.code || ''} {getTaskByCode(entry.task_type)?.name || entry.task_type}
                                                </span>
                                                {entry.is_running && (
                                                    <Badge className="bg-blue-100 text-indigo-700 border-0 text-xs">Running</Badge>
                                                )}
                                                {entry.is_manual && !entry.is_running && !entry.is_waived && (
                                                    <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">Manual</Badge>
                                                )}
                                                {entry.is_waived && (
                                                    <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">No Charge</Badge>
                                                )}
                                                {entry.is_invoiced && !entry.is_waived && (
                                                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Invoiced</Badge>
                                                )}
                                            </div>
                                            <p className={`text-sm ${entry.is_waived ? 'text-muted-foreground/60 line-through' : 'text-muted-foreground'}`}>
                                                {entry.notes || entry.description || <span className="italic text-muted-foreground/50">No notes</span>}
                                            </p>
                                            {entry.is_waived && entry.waive_reason && (
                                                <p className="text-xs text-amber-600 mt-1 italic">
                                                    {entry.waive_reason}
                                                </p>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-2">
                                                {formatDateTime(entry.start_time || entry.created_at) || 'Manual entry'}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <span className={`font-display text-lg font-semibold ${entry.is_waived ? 'line-through text-muted-foreground' : ''}`}>
                                                {entry.is_running ? '...' : formatDuration(entry.duration_minutes)}
                                            </span>
                                            {!entry.is_running && entry.billable_amount > 0 && (
                                                <span className={`text-xs font-display ${entry.is_waived ? 'line-through text-muted-foreground' : 'text-emerald-600'}`}>
                                                    {formatMoney(entry.billable_amount)}
                                                </span>
                                            )}
                                            {!entry.is_running && (
                                                <div className="flex items-center gap-3 mt-1">
                                                    <button
                                                        onClick={() => handleWaiveEntry(entry.id, entry.is_waived)}
                                                        className={`p-2 rounded-full transition-colors ${
                                                            entry.is_waived 
                                                                ? 'text-amber-600 hover:bg-amber-50' 
                                                                : 'text-muted-foreground hover:text-amber-600 hover:bg-amber-50'
                                                        }`}
                                                        title={entry.is_waived ? 'Restore to billable' : 'Mark as no charge'}
                                                        data-testid={`waive-entry-${entry.id}`}
                                                    >
                                                        {entry.is_waived ? <RotateCcw className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                                                    </button>
                                                    <button
                                                        onClick={() => confirmDeleteEntry(entry)}
                                                        className="p-2 text-muted-foreground hover:text-destructive rounded-full hover:bg-red-50 transition-colors"
                                                        data-testid={`delete-entry-${entry.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Invoices Tab */}
                {activeTab === 'invoices' && (
                    <div className="space-y-4 animate-fade-in-up">
                        {/* Create Invoice Button */}
                        <button
                            onClick={() => setIsExportOpen(true)}
                            disabled={entries.filter(e => !e.is_running).length === 0 || isCaseClosed}
                            className="w-full py-4 bg-[#1C6EE7] text-white rounded-2xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="create-invoice-btn"
                        >
                            <Plus className="h-5 w-5" />
                            {isCaseClosed ? 'Case Closed - View Only' : 'Create New Invoice'}
                        </button>

                        {/* Summary Stats */}
                        {invoices.length > 0 && (
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white rounded-2xl p-4 shadow-soft">
                                    <p className="text-xs text-muted-foreground mb-1">Total Invoiced</p>
                                    <p className="font-display text-xl font-bold text-[#1C6EE7]">
                                        {formatMoney(invoices.reduce((sum, inv) => sum + (inv.amount_due || 0), 0))}
                                    </p>
                                </div>
                                <div className="bg-white rounded-2xl p-4 shadow-soft">
                                    <p className="text-xs text-muted-foreground mb-1">Outstanding</p>
                                    <p className="font-display text-xl font-bold text-amber-600">
                                        {formatMoney(invoices.filter(i => i.status !== 'paid').reduce((sum, inv) => sum + (inv.amount_due || 0), 0))}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Invoices List */}
                        {invoices.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-3xl shadow-soft">
                                <Receipt className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                                <p className="text-muted-foreground mb-2">No invoices yet</p>
                                <p className="text-xs text-muted-foreground">Create your first invoice to start tracking payments</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-heading font-semibold">All Invoices</h3>
                                    <Badge variant="secondary" className="text-xs">
                                        {invoices.filter(i => i.status === 'paid').length}/{invoices.length} paid
                                    </Badge>
                                </div>
                                {invoices.map(inv => (
                                    <div 
                                        key={inv.id} 
                                        className={`p-4 rounded-2xl shadow-soft transition-all ${
                                            inv.status === 'paid' 
                                                ? 'bg-emerald-50 border border-emerald-200' 
                                                : 'bg-rose-50 border border-rose-200'
                                        }`}
                                        data-testid={`invoice-${inv.id}`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <p className="font-display text-sm font-semibold">{inv.invoice_number}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    Created {format(new Date(inv.created_at), 'MMM d, yyyy')}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleViewInvoiceDetails(inv)}
                                                    className="p-2 text-slate-500 hover:text-[#1C6EE7] hover:bg-slate-100 rounded-full transition-colors"
                                                    title="Quick Look"
                                                    data-testid={`view-invoice-${inv.id}`}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                {inv.status === 'paid' ? (
                                                    <Badge className="bg-emerald-100 text-emerald-700 border-0">
                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                        Paid
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-rose-100 text-rose-700 border-0">Unpaid</Badge>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-end justify-between">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Amount Due</p>
                                                <p className="font-display text-2xl font-bold">{formatMoney(inv.amount_due)}</p>
                                                {inv.amount_waived > 0 && (
                                                    <p className="text-xs text-amber-600">
                                                        {formatMoney(inv.amount_waived)} waived
                                                    </p>
                                                )}
                                            </div>
                                            {inv.status !== 'paid' && (
                                                <button
                                                    onClick={() => confirmMarkPaid(inv)}
                                                    className="px-4 py-2 text-sm font-medium bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors flex items-center gap-2"
                                                    data-testid={`mark-paid-${inv.id}`}
                                                >
                                                    <CheckCircle className="h-4 w-4" />
                                                    Mark Paid
                                                </button>
                                            )}
                                            {inv.status === 'paid' && inv.paid_at && (
                                                <p className="text-xs text-emerald-600">
                                                    Paid on {format(new Date(inv.paid_at), 'MMM d, yyyy')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Reminders Tab */}
                {activeTab === 'reminders' && (
                    <div className="space-y-4 animate-fade-in-up">
                        {/* Action Buttons */}
                        {!isCaseClosed && (
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setIsReminderOpen(true)}
                                    className="flex-1 py-3 bg-white rounded-2xl shadow-soft font-medium flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
                                    data-testid="add-reminder-btn"
                                >
                                    <Plus className="h-5 w-5 text-[#1C6EE7]" />
                                    Reminder
                                </button>
                                <button
                                    onClick={() => setIsStatuteOpen(true)}
                                    className="flex-1 py-3 bg-white rounded-2xl shadow-soft font-medium flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
                                    data-testid="add-statute-btn"
                                >
                                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                                    Statute
                            </button>
                        </div>
                        )}

                        {/* Reminders List */}
                        {reminders.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-3xl shadow-soft">
                                <Bell className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                                <p className="text-muted-foreground">No deadlines yet</p>
                            </div>
                        ) : (
                            reminders.map(reminder => {
                                const days = getDaysUntil(reminder.due_date);
                                const isOverdue = days !== null && days < 0;
                                const isUrgent = days !== null && days >= 0 && days <= 7;
                                
                                return (
                                    <div
                                        key={reminder.id}
                                        className={`p-4 rounded-2xl flex items-start gap-3 ${
                                            reminder.is_completed ? 'bg-slate-50' : isOverdue ? 'bg-red-50' : 'bg-white'
                                        } shadow-soft`}
                                        data-testid={`reminder-${reminder.id}`}
                                    >
                                        <button
                                            onClick={() => handleToggleReminder(reminder.id, reminder.is_completed)}
                                            className={`w-6 h-6 min-w-6 min-h-6 rounded-full flex items-center justify-center transition-colors shrink-0 border-2 ${
                                                reminder.is_completed
                                                    ? 'bg-emerald-500 border-emerald-500 text-white'
                                                    : 'border-slate-300 hover:border-primary'
                                            }`}
                                        >
                                            {reminder.is_completed && <CheckCircle className="h-4 w-4" />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h4 className={`font-medium ${reminder.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                                                    {reminder.title}
                                                </h4>
                                                {reminder.is_critical && !reminder.is_completed && (
                                                    <Badge className="bg-blue-100 text-[#1C6EE7] border-0 text-xs">Critical</Badge>
                                                )}
                                            </div>
                                            {reminder.description && (
                                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{reminder.description}</p>
                                            )}
                                            <p className={`text-sm mt-2 font-display ${
                                                reminder.is_completed ? 'text-muted-foreground' : isOverdue ? 'text-red-600 font-semibold' : isUrgent ? 'text-amber-600 font-semibold' : 'text-muted-foreground'
                                            }`}>
                                                {format(new Date(reminder.due_date), 'MMM d, yyyy')}
                                                {!reminder.is_completed && days !== null && (
                                                    <span className="ml-2">
                                                        ({isOverdue ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d left`})
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteReminder(reminder.id)}
                                            className="min-w-8 min-h-8 w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-destructive rounded-full hover:bg-red-50 transition-colors shrink-0 aspect-square"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Delete Case Button */}
                <div className="mt-8 pt-8 border-t border-border/50">
                    <button
                        onClick={() => setIsDeleteOpen(true)}
                        className="w-full py-3 text-destructive font-medium rounded-2xl hover:bg-red-50 transition-colors"
                        data-testid="delete-case-btn"
                    >
                        Delete Case
                    </button>
                </div>

                <div className="h-24" />
            </main>

            <BottomNav />

            {/* Stop Timer Dialog */}
            <Dialog open={isStopDialogOpen} onOpenChange={setIsStopDialogOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto ">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Stop Timer</DialogTitle>
                        <DialogDescription>Add notes before stopping • tap mic to dictate</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <VoiceInputInline
                            value={stopNotes}
                            onChange={setStopNotes}
                            placeholder="What did you work on?"
                        />
                        <Button onClick={handleStopTimer} disabled={isStopping} className="w-full h-12 rounded-full bg-red-500 hover:bg-red-600">
                            {isStopping ? 'Stopping...' : 'Stop Timer'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Manual Entry Dialog */}
            <Dialog open={isManualOpen} onOpenChange={setIsManualOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto ">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Add Time</DialogTitle>
                        <DialogDescription>Use the dial to set duration</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <TouchDial value={dialMinutes} onChange={setDialMinutes} maxMinutes={480} />
                        
                        <div className="space-y-2">
                            <Label>Task Type</Label>
                            <Select value={manualTask} onValueChange={setManualTask}>
                                <SelectTrigger className="h-12 rounded-xl w-full">
                                    <SelectValue>
                                        {getTaskByCode(manualTask) && (
                                            <span className="font-medium">{getTaskLabel(getTaskByCode(manualTask))}</span>
                                        )}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                    {TASK_TYPES.map(t => (
                                        <SelectItem key={t.code} value={t.code} className="py-2.5">
                                            <span className="font-medium">{t.code} - {t.name}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {getTaskByCode(manualTask) && (
                                <p className="text-xs text-muted-foreground px-1">
                                    {getTaskByCode(manualTask).description}
                                </p>
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                Notes
                                <span className="text-xs text-muted-foreground">• tap mic to dictate</span>
                            </Label>
                            <VoiceInputInline
                                value={manualNotes}
                                onChange={setManualNotes}
                                placeholder="What did you work on?"
                            />
                        </div>
                        
                        <Button onClick={handleManualEntry} disabled={isSubmitting} className="w-full h-12 rounded-full">
                            {isSubmitting ? 'Adding...' : `Add ${dialMinutes} Minutes`}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Reminder Dialog */}
            <Dialog open={isReminderOpen} onOpenChange={setIsReminderOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto ">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Add Reminder</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label>Title</Label>
                            <Input value={reminderTitle} onChange={(e) => setReminderTitle(e.target.value)} className="h-12 rounded-xl w-full" />
                        </div>
                        <div className="space-y-2">
                            <Label>Due Date</Label>
                            <Input type="date" value={reminderDueDate} onChange={(e) => setReminderDueDate(e.target.value)} className="h-12 rounded-xl w-full" />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea value={reminderDescription} onChange={(e) => setReminderDescription(e.target.value)} rows={3} className="rounded-xl w-full" />
                        </div>
                        <div className="flex items-center gap-3">
                            <Checkbox id="critical" checked={reminderIsCritical} onCheckedChange={setReminderIsCritical} />
                            <Label htmlFor="critical" className="text-sm font-normal">Mark as critical</Label>
                        </div>
                        <Button onClick={handleCreateReminder} disabled={isSubmitting} className="w-full h-12 rounded-full">
                            {isSubmitting ? 'Creating...' : 'Add Reminder'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Statute Dialog */}
            <Dialog open={isStatuteOpen} onOpenChange={setIsStatuteOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto ">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Add Statute Deadline</DialogTitle>
                        <DialogDescription>
                            Select from templates or enter a custom deadline
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {/* Caution Notice */}
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                            <p className="text-xs text-amber-800 flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                <span><strong>Important:</strong> Please independently verify all statutory deadlines with current laws and regulations.</span>
                            </p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Case Type</Label>
                            <Select value={selectedCaseType} onValueChange={(v) => { setSelectedCaseType(v); setSelectedTemplate(''); }}>
                                <SelectTrigger className="h-12 rounded-xl w-full"><SelectValue placeholder="Select type" /></SelectTrigger>
                                <SelectContent className="max-h-[40vh] overflow-y-auto">
                                    {Object.keys(statuteTemplates).sort().map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {selectedCaseType && currentTemplates.length > 0 && (
                            <div className="space-y-2">
                                <Label>Statute</Label>
                                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                                    <SelectTrigger className="h-12 rounded-xl w-full"><SelectValue placeholder="Select deadline" /></SelectTrigger>
                                    <SelectContent>
                                        {currentTemplates.map(t => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>Incident Date</Label>
                            <Input type="date" value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} className="h-12 rounded-xl w-full" />
                        </div>
                        <Button onClick={handleApplyStatute} disabled={isSubmitting || !selectedCaseType || !selectedTemplate || !incidentDate} className="w-full h-12 rounded-full">
                            {isSubmitting ? 'Adding...' : 'Add Deadline'}
                        </Button>
                        
                        {/* Manual Entry Option */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background px-2 text-muted-foreground">or</span>
                            </div>
                        </div>
                        <button
                            onClick={() => { setIsStatuteOpen(false); setIsManualStatuteOpen(true); }}
                            className="w-full py-3 text-sm text-[#1C6EE7] font-medium hover:bg-blue-50 rounded-xl transition-colors"
                        >
                            Enter Custom Deadline Manually
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Manual Statute Dialog */}
            <Dialog open={isManualStatuteOpen} onOpenChange={setIsManualStatuteOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto ">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Custom Statute Deadline</DialogTitle>
                        <DialogDescription>
                            Enter your own deadline with specific dates
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {/* Caution Notice */}
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                            <p className="text-xs text-amber-800 flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                <span><strong>Important:</strong> Please independently verify all statutory deadlines. This tool is for organizational purposes only and should not be relied upon as legal advice.</span>
                            </p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Deadline Title <span className="text-destructive">*</span></Label>
                            <Input 
                                value={manualStatuteTitle} 
                                onChange={(e) => setManualStatuteTitle(e.target.value)} 
                                placeholder="e.g., Filing Deadline, SOL Expires"
                                className="h-12 rounded-xl w-full" 
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description (optional)</Label>
                            <Textarea 
                                value={manualStatuteDescription} 
                                onChange={(e) => setManualStatuteDescription(e.target.value)} 
                                placeholder="Additional details about this deadline..."
                                rows={2}
                                className="rounded-xl w-full" 
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Incident/Trigger Date (optional)</Label>
                            <Input 
                                type="date" 
                                value={manualStatuteIncidentDate} 
                                onChange={(e) => setManualStatuteIncidentDate(e.target.value)} 
                                className="h-12 rounded-xl w-full" 
                            />
                            <p className="text-xs text-muted-foreground">The date the event occurred that triggered this deadline</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Statute Deadline Date <span className="text-destructive">*</span></Label>
                            <Input 
                                type="date" 
                                value={manualStatuteDeadline} 
                                onChange={(e) => setManualStatuteDeadline(e.target.value)} 
                                className="h-12 rounded-xl w-full" 
                            />
                            <p className="text-xs text-muted-foreground">The actual deadline date</p>
                        </div>
                        <Button 
                            onClick={handleCreateManualStatute} 
                            disabled={isSubmitting || !manualStatuteTitle || !manualStatuteDeadline} 
                            className="w-full h-12 rounded-full"
                        >
                            {isSubmitting ? 'Adding...' : 'Add Custom Deadline'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Case Dialog */}
            <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Case?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete this case and all associated time entries and reminders.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteCase} className="rounded-full bg-destructive hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Time Entry Confirmation Dialog */}
            <AlertDialog open={isDeleteEntryOpen} onOpenChange={setIsDeleteEntryOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Time Entry?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {entryToDelete && (
                                <>
                                    This will permanently delete the <strong>{getTaskByCode(entryToDelete.task_type)?.name || entryToDelete.task_type}</strong> entry
                                    {entryToDelete.duration_minutes && ` (${formatDuration(entryToDelete.duration_minutes)})`}.
                                    {entryToDelete.billable_amount > 0 && !entryToDelete.is_waived && (
                                        <> This will remove <strong>{formatMoney(entryToDelete.billable_amount)}</strong> from your billable amount.</>
                                    )}
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full" onClick={() => setEntryToDelete(null)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteEntry} className="rounded-full bg-destructive hover:bg-destructive/90">
                            Delete Entry
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Payment Confirmation Dialog */}
            <AlertDialog open={isPaymentConfirmOpen} onOpenChange={setIsPaymentConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                            Confirm Payment Received
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {invoiceToMarkPaid && (
                                <div className="space-y-2 mt-2">
                                    <p>Mark this invoice as paid?</p>
                                    <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                                        <p className="font-display text-sm font-medium text-foreground">{invoiceToMarkPaid.invoice_number}</p>
                                        <p className="text-lg font-bold text-foreground">{formatMoney(invoiceToMarkPaid.amount_due)}</p>
                                    </div>
                                    <p className="text-xs">This action will record the payment date as today.</p>
                                </div>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full" onClick={() => setInvoiceToMarkPaid(null)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleMarkInvoicePaid} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
                            Confirm Payment
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Add Retainer Payment Dialog */}
            <Dialog open={isRetainerOpen} onOpenChange={setIsRetainerOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto ">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <Wallet className="h-5 w-5 text-[#1C6EE7]" />
                            Add Retainer Payment
                        </DialogTitle>
                        <DialogDescription>
                            Add funds to the retainer balance for this case
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label>Payment Amount ($)</Label>
                            <Input
                                type="number"
                                placeholder="e.g., 2500"
                                value={retainerPayment}
                                onChange={(e) => setRetainerPayment(e.target.value)}
                                className="h-12 rounded-xl font-display text-lg w-full"
                                data-testid="retainer-amount-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Input
                                placeholder="e.g., Wire transfer #12345"
                                value={retainerNotes}
                                onChange={(e) => setRetainerNotes(e.target.value)}
                                className="h-12 rounded-xl w-full"
                            />
                        </div>
                        
                        {/* Current Balance Info */}
                        <div className="bg-slate-50 rounded-xl p-4">
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-muted-foreground">Current Balance</span>
                                <span className="font-display font-medium">{formatMoney(caseData?.retainer_balance || 0)}</span>
                            </div>
                            {retainerPayment && parseFloat(retainerPayment) > 0 && (
                                <div className="flex justify-between text-sm pt-2 border-t">
                                    <span className="text-emerald-600 font-medium">New Balance</span>
                                    <span className="font-display font-semibold text-emerald-600">
                                        {formatMoney((caseData?.retainer_balance || 0) + parseFloat(retainerPayment))}
                                    </span>
                                </div>
                            )}
                        </div>

                        <Button 
                            onClick={handleAddRetainer} 
                            disabled={isSubmitting || !retainerPayment || parseFloat(retainerPayment) <= 0} 
                            className="w-full h-12 rounded-full bg-[#1C6EE7]"
                            data-testid="submit-retainer-btn"
                        >
                            {isSubmitting ? 'Processing...' : `Add ${retainerPayment ? formatMoney(parseFloat(retainerPayment)) : '$0'}`}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Export Dialog */}
            <Dialog open={isExportOpen} onOpenChange={(open) => {
                setIsExportOpen(open);
                if (!open) setExportDateAccordion(null);
            }}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-md max-h-[85vh] overflow-y-auto rounded-3xl p-5">
                    <DialogHeader className="text-center pb-2">
                        <DialogTitle className="font-heading text-xl flex items-center justify-center gap-2">
                            <Download className="h-5 w-5 text-[#1C6EE7]" />
                            Export Time Entries
                        </DialogTitle>
                        <DialogDescription className="text-center text-sm">
                            Download as CSV or generate a PDF invoice
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        {/* Show entries date range hint */}
                        {getEntriesDateRange() && (
                            <div className="bg-blue-50 rounded-xl px-4 py-2.5 text-center">
                                <p className="text-xs text-blue-700">
                                    Entries available: <span className="font-semibold">{format(new Date(getEntriesDateRange().start), 'MMM d')}</span> – <span className="font-semibold">{format(new Date(getEntriesDateRange().end), 'MMM d, yyyy')}</span>
                                </p>
                            </div>
                        )}
                        
                        {/* Date Range Filter - Accordion Style */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-center gap-2 text-xs font-medium text-slate-500 mb-2">
                                <CalendarIcon className="h-3.5 w-3.5" />
                                Filter by Date (optional)
                            </div>
                            
                            {/* From Date Accordion */}
                            <div className="bg-slate-50 rounded-xl overflow-hidden">
                                <button
                                    onClick={() => setExportDateAccordion(exportDateAccordion === 'from' ? null : 'from')}
                                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-100 transition-colors"
                                    data-testid="export-start-date-toggle"
                                >
                                    <span className="text-sm font-medium text-slate-700">From</span>
                                    <div className="flex items-center gap-2">
                                        {exportStartDate ? (
                                            <span className="text-sm font-semibold text-[#1C6EE7]">
                                                {format(new Date(exportStartDate), 'MMM d, yyyy')}
                                            </span>
                                        ) : (
                                            <span className="text-sm text-slate-400">Any</span>
                                        )}
                                        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${exportDateAccordion === 'from' ? 'rotate-90' : ''}`} />
                                    </div>
                                </button>
                                {exportDateAccordion === 'from' && (
                                    <div className="px-2 pb-3 flex justify-center animate-in slide-in-from-top-2 duration-200">
                                        <Calendar
                                            mode="single"
                                            selected={exportStartDate ? new Date(exportStartDate) : undefined}
                                            onSelect={(date) => {
                                                setExportStartDate(date ? format(date, 'yyyy-MM-dd') : '');
                                                setExportDateAccordion(null);
                                            }}
                                            className="rounded-xl border bg-white"
                                            data-testid="export-start-date"
                                        />
                                    </div>
                                )}
                            </div>
                            
                            {/* To Date Accordion */}
                            <div className="bg-slate-50 rounded-xl overflow-hidden">
                                <button
                                    onClick={() => setExportDateAccordion(exportDateAccordion === 'to' ? null : 'to')}
                                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-100 transition-colors"
                                    data-testid="export-end-date-toggle"
                                >
                                    <span className="text-sm font-medium text-slate-700">To</span>
                                    <div className="flex items-center gap-2">
                                        {exportEndDate ? (
                                            <span className="text-sm font-semibold text-[#1C6EE7]">
                                                {format(new Date(exportEndDate), 'MMM d, yyyy')}
                                            </span>
                                        ) : (
                                            <span className="text-sm text-slate-400">Any</span>
                                        )}
                                        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${exportDateAccordion === 'to' ? 'rotate-90' : ''}`} />
                                    </div>
                                </button>
                                {exportDateAccordion === 'to' && (
                                    <div className="px-2 pb-3 flex justify-center animate-in slide-in-from-top-2 duration-200">
                                        <Calendar
                                            mode="single"
                                            selected={exportEndDate ? new Date(exportEndDate) : undefined}
                                            onSelect={(date) => {
                                                setExportEndDate(date ? format(date, 'yyyy-MM-dd') : '');
                                                setExportDateAccordion(null);
                                            }}
                                            className="rounded-xl border bg-white"
                                            data-testid="export-end-date"
                                        />
                                    </div>
                                )}
                            </div>
                            
                            {(exportStartDate || exportEndDate) && (
                                <button
                                    onClick={() => { setExportStartDate(''); setExportEndDate(''); }}
                                    className="text-xs text-[#1C6EE7] hover:underline w-full text-center py-2"
                                >
                                    Clear dates (export all)
                                </button>
                            )}
                        </div>

                        {/* Export Options */}
                        <div className="space-y-3">
                            <Button 
                                onClick={handleExportCSV} 
                                disabled={isExporting}
                                variant="outline"
                                className="w-full h-14 rounded-xl flex items-center justify-center gap-3"
                                data-testid="export-csv-btn"
                            >
                                <Download className="h-5 w-5 text-emerald-600" />
                                <div className="text-left">
                                    <p className="font-medium text-sm">Export CSV</p>
                                    <p className="text-xs text-muted-foreground">Spreadsheet format</p>
                                </div>
                            </Button>
                            
                            <Button 
                                onClick={() => handleCreateInvoice(false)} 
                                disabled={isExporting}
                                className="w-full h-16 rounded-xl flex flex-col items-center justify-center gap-1 px-6 bg-[#1C6EE7]"
                                data-testid="create-invoice-btn"
                            >
                                <FileCheck className="h-5 w-5" />
                                <div className="text-center">
                                    <p className="font-medium text-sm">Create & Download Invoice</p>
                                    <p className="text-xs text-white/80">PDF with payment tracking</p>
                                </div>
                            </Button>
                            
                            <button 
                                onClick={() => handleExportInvoice(false)} 
                                disabled={isExporting}
                                className="w-full text-sm text-muted-foreground hover:text-[#1C6EE7] transition-colors text-center py-3"
                                data-testid="export-invoice-btn"
                            >
                                Download PDF only (no tracking)
                            </button>
                        </div>

                        {isExporting && (
                            <div className="text-center text-sm text-muted-foreground">
                                Generating...
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Duplicate Invoice Warning Dialog */}
            <AlertDialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                            <AlertTriangle className="h-5 w-5" />
                            Duplicate Invoice Warning
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            {invoicePreview && (
                                <>
                                    <p className="text-slate-700">
                                        <strong>{invoicePreview.already_invoiced_count}</strong> of {invoicePreview.entry_count} time entries have already been invoiced.
                                    </p>
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                                        <p className="font-medium text-amber-800 mb-2">Already invoiced entries:</p>
                                        <ul className="space-y-1 text-amber-700">
                                            {invoicePreview.already_invoiced_entries?.slice(0, 5).map((entry, i) => (
                                                <li key={i}>
                                                    • {entry.date}: {entry.task_type} (${entry.amount?.toFixed(2)})
                                                </li>
                                            ))}
                                            {invoicePreview.already_invoiced_entries?.length > 5 && (
                                                <li className="text-amber-600">
                                                    ...and {invoicePreview.already_invoiced_entries.length - 5} more
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                    <p className="text-slate-600">
                                        Creating this invoice will result in <strong>duplicate billing</strong> for these entries. 
                                        Are you sure you want to proceed?
                                    </p>
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel 
                            onClick={() => { setShowDuplicateWarning(false); setInvoicePreview(null); }}
                            className="rounded-full"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => handleCreateInvoice(true)}
                            className="rounded-full bg-amber-600 hover:bg-amber-700"
                        >
                            Create Anyway
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Invoice Details Dialog */}
            <Dialog open={isInvoiceDetailsOpen} onOpenChange={(open) => {
                setIsInvoiceDetailsOpen(open);
                if (!open) {
                    setShowDeleteInvoiceConfirm(false);
                    setDeleteInvoiceConfirmText('');
                }
            }}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Receipt className="h-5 w-5 text-[#1C6EE7]" />
                            Invoice Details
                        </DialogTitle>
                    </DialogHeader>
                    {selectedInvoiceDetails && (
                        <div className="space-y-4">
                            {/* Invoice Header */}
                            <div className={`p-4 rounded-2xl ${
                                selectedInvoiceDetails.status === 'paid' 
                                    ? 'bg-emerald-50 border border-emerald-200' 
                                    : 'bg-rose-50 border border-rose-200'
                            }`}>
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <p className="font-display text-lg font-bold">{selectedInvoiceDetails.invoice_number}</p>
                                        <p className="text-xs text-muted-foreground">
                                            Case: {caseData?.name}
                                        </p>
                                    </div>
                                    <Badge className={selectedInvoiceDetails.status === 'paid' 
                                        ? 'bg-emerald-100 text-emerald-700 border-0' 
                                        : 'bg-rose-100 text-rose-700 border-0'
                                    }>
                                        {selectedInvoiceDetails.status === 'paid' ? (
                                            <><CheckCircle className="h-3 w-3 mr-1" /> Paid</>
                                        ) : 'Unpaid'}
                                    </Badge>
                                </div>
                            </div>

                            {/* Invoice Details */}
                            <div className="space-y-3">
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-muted-foreground">Created</span>
                                    <span className="font-medium">
                                        {format(new Date(selectedInvoiceDetails.created_at), 'MMM d, yyyy')}
                                    </span>
                                </div>
                                {selectedInvoiceDetails.start_date && (
                                    <div className="flex justify-between py-2 border-b">
                                        <span className="text-muted-foreground">Billing Period</span>
                                        <span className="font-medium">
                                            {selectedInvoiceDetails.start_date} to {selectedInvoiceDetails.end_date || 'Present'}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-muted-foreground">Time Entries</span>
                                    <span className="font-medium">{selectedInvoiceDetails.entries_count || '-'}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-muted-foreground">Total Hours</span>
                                    <span className="font-medium">
                                        {selectedInvoiceDetails.total_hours 
                                            ? `${selectedInvoiceDetails.total_hours.toFixed(2)} hrs`
                                            : '-'
                                        }
                                    </span>
                                </div>
                                {selectedInvoiceDetails.amount_waived > 0 && (
                                    <div className="flex justify-between py-2 border-b">
                                        <span className="text-amber-600">Waived</span>
                                        <span className="font-medium text-amber-600">
                                            -{formatMoney(selectedInvoiceDetails.amount_waived)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between py-3 bg-slate-50 rounded-xl px-3 -mx-1">
                                    <span className="font-semibold">Amount Due</span>
                                    <span className="font-display text-xl font-bold text-[#1C6EE7]">
                                        {formatMoney(selectedInvoiceDetails.amount_due)}
                                    </span>
                                </div>
                                {selectedInvoiceDetails.status === 'paid' && selectedInvoiceDetails.paid_at && (
                                    <div className="flex justify-between py-2">
                                        <span className="text-emerald-600">Paid On</span>
                                        <span className="font-medium text-emerald-600">
                                            {format(new Date(selectedInvoiceDetails.paid_at), 'MMM d, yyyy')}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3 pt-2">
                                <div className="flex gap-3">
                                    <Button
                                        onClick={() => handleDownloadInvoice(selectedInvoiceDetails)}
                                        className="flex-1 h-12 rounded-xl bg-[#1C6EE7] hover:bg-[#1557b8]"
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        Download PDF
                                    </Button>
                                    {selectedInvoiceDetails.status !== 'paid' && (
                                        <Button
                                            onClick={() => {
                                                setIsInvoiceDetailsOpen(false);
                                                confirmMarkPaid(selectedInvoiceDetails);
                                            }}
                                            variant="outline"
                                            className="flex-1 h-12 rounded-xl border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                                        >
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            Mark Paid
                                        </Button>
                                    )}
                                </div>
                                
                                {/* Delete Invoice Section */}
                                {!showDeleteInvoiceConfirm ? (
                                    <button
                                        onClick={() => setShowDeleteInvoiceConfirm(true)}
                                        className="w-full text-center text-sm text-red-500 hover:text-red-600 py-2"
                                    >
                                        Delete Invoice
                                    </button>
                                ) : (
                                    <div className="bg-red-50 rounded-xl p-4 space-y-3 animate-in slide-in-from-top-2">
                                        <p className="text-sm text-red-700 text-center">
                                            Type <span className="font-mono font-bold bg-red-100 px-1.5 py-0.5 rounded">DELETE</span> to confirm
                                        </p>
                                        <Input
                                            value={deleteInvoiceConfirmText}
                                            onChange={(e) => setDeleteInvoiceConfirmText(e.target.value.toUpperCase())}
                                            placeholder="Type DELETE"
                                            className="h-11 rounded-xl text-center font-mono border-red-200 focus:border-red-400"
                                        />
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setShowDeleteInvoiceConfirm(false);
                                                    setDeleteInvoiceConfirmText('');
                                                }}
                                                className="flex-1 h-10 rounded-xl"
                                                disabled={isDeletingInvoice}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleDeleteInvoice}
                                                disabled={deleteInvoiceConfirmText !== 'DELETE' || isDeletingInvoice}
                                                className="flex-1 h-10 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-red-300"
                                            >
                                                {isDeletingInvoice ? 'Deleting...' : 'Delete'}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
